import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { FileSpreadsheet, Download, Filter, RotateCcw, Clock, User, Calendar } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import { STATUS_COLORS_SOFT } from '../utils/constants';

// Timesheet historically rendered "pending" with a yellow tint instead of
// slate; keep that look unchanged by overriding just that key.
const STATUS_COLORS = { ...STATUS_COLORS_SOFT, pending: 'bg-yellow-100 text-yellow-700' };

export default function Timesheet() {
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ member_id: '', client_id: '', status: '', date_from: '', date_to: '' });

  useEffect(() => { fetchInitialData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInitialData = async () => {
    try {
      const [usersRes, clientsRes] = await Promise.all([api.get('/users'), api.get('/clients')]);
      setUsers(usersRes.data);
      setClients(clientsRes.data);
      await fetchTimesheet({});
    } catch (error) { toast.error('Failed to load data'); }
  };

  const fetchTimesheet = async (params) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => { if (v) queryParams.append(k, v); });
      const res = await api.get(`/reports/timesheet?${queryParams.toString()}`);
      setEntries(res.data);
    } catch (error) { toast.error('Failed to load timesheet'); }
    finally { setLoading(false); }
  };

  const handleFilter = () => fetchTimesheet(filters);
  const resetFilters = () => { setFilters({ member_id: '', client_id: '', status: '', date_from: '', date_to: '' }); fetchTimesheet({}); };

  const downloadCSV = () => {
    if (entries.length === 0) { toast.error('No data to download'); return; }

    const headers = ['Member', 'Task', 'Service', 'Client', 'Start Date', 'End Date', 'Total Hours', 'Status'];
    const rows = entries.map(e => [
      e.member_name, e.task_title, e.service_name, e.client_name,
      e.start_date || e.date || '',
      (e.task_status === 'completed' ? (e.end_date || '') : ''),
      e.hours, e.task_status,
    ]);

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timesheet_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Timesheet downloaded');
  };

  const [exporting, setExporting] = useState(false);
  const [bulkExporting, setBulkExporting] = useState(false);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSA = currentUser.role === 'super_admin';

  const downloadExcel = async () => {
    if (!filters.member_id) {
      toast.error('Pick an employee first to download their timesheet.');
      return;
    }
    if (exporting) return;
    const start = filters.date_from || new Date(new Date().setDate(1)).toISOString().slice(0, 10);
    const end = filters.date_to || new Date().toISOString().slice(0, 10);
    setExporting(true);
    try {
      const res = await api.get(`/timesheet/export?user_id=${filters.member_id}&start_date=${start}&end_date=${end}`,
        { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/i);
      const filename = match ? match[1] : `timesheet_${start}_to_${end}.xlsx`;
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel timesheet downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to download Excel');
    } finally {
      setExporting(false);
    }
  };

  const totalHours = entries.reduce((sum, e) => sum + e.hours, 0);

  const downloadBulkZip = async () => {
    if (bulkExporting) return;
    const start = filters.date_from || new Date(new Date().setDate(1)).toISOString().slice(0, 10);
    const end = filters.date_to || new Date().toISOString().slice(0, 10);
    setBulkExporting(true);
    try {
      const res = await api.get(`/timesheet/export/bulk?start_date=${start}&end_date=${end}`,
        { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/i);
      const filename = match ? match[1] : `payroll_timesheets_${start}_to_${end}.zip`;
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success('Bulk timesheet ZIP downloaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to download bulk ZIP');
    } finally {
      setBulkExporting(false);
    }
  };

  return (
    <div className="p-8 min-h-full" style={{
        background: 'linear-gradient(135deg, rgba(91, 111, 214, 0.03) 0%, rgba(16, 185, 129, 0.03) 100%)'
      }}>
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold tracking-tight mb-2" style={{
              background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
            }}>Timesheet Report</h1>
            <p className="text-lg text-slate-600">Track and download work logs for your team</p>
          </div>
          <div className="flex gap-2">
            {isSA && (
              <Button onClick={downloadBulkZip} disabled={bulkExporting} variant="outline" className="border-[#5B6FD6] text-[#5B6FD6] hover:bg-[#5B6FD6]/10 disabled:opacity-60" data-testid="download-bulk-zip-btn">
                <Download className="h-5 w-5 mr-2" /> {bulkExporting ? 'Zipping…' : 'Bulk ZIP (all employees)'}
              </Button>
            )}
            <Button onClick={downloadExcel} disabled={exporting} className="bg-[#5B6FD6] hover:bg-[#4A5BC4] shadow-md disabled:opacity-60" data-testid="download-xlsx-btn">
              <Download className="h-5 w-5 mr-2" /> {exporting ? 'Preparing…' : 'Download Excel'}
            </Button>
            <Button onClick={downloadCSV} variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 shadow-sm" data-testid="download-csv-btn">
              <Download className="h-5 w-5 mr-2" /> CSV (flat)
            </Button>
          </div>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <Card className="border-slate-200 shadow-sm bg-white/80">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-[#5B6FD6]/10 flex items-center justify-center"><FileSpreadsheet className="h-6 w-6 text-[#5B6FD6]" /></div>
              <div><p className="text-sm text-slate-500">Total Entries</p><p className="text-2xl font-bold">{entries.length}</p></div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-sm bg-white/80">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-amber-50 flex items-center justify-center"><Clock className="h-6 w-6 text-amber-600" /></div>
              <div><p className="text-sm text-slate-500">Total Hours</p><p className="text-2xl font-bold">{totalHours.toFixed(1)}h</p></div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 shadow-sm bg-white/80">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-green-50 flex items-center justify-center"><User className="h-6 w-6 text-green-600" /></div>
              <div><p className="text-sm text-slate-500">Unique Members</p><p className="text-2xl font-bold">{new Set(entries.map(e => e.member_id)).size}</p></div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-slate-200 shadow-sm bg-white/80 mb-6" data-testid="timesheet-filters">
          <CardContent className="p-5">
            <div className="flex items-end gap-4 flex-wrap">
              <div className="flex-1 min-w-[150px]">
                <Label className="text-xs text-slate-500">Member</Label>
                <Select value={filters.member_id} onValueChange={(v) => setFilters({ ...filters, member_id: v === 'all' ? '' : v })}>
                  <SelectTrigger data-testid="ts-filter-member"><SelectValue placeholder="All Members" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Members</SelectItem>
                    {users.map(u => (<SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[150px]">
                <Label className="text-xs text-slate-500">Client</Label>
                <Select value={filters.client_id} onValueChange={(v) => setFilters({ ...filters, client_id: v === 'all' ? '' : v })}>
                  <SelectTrigger data-testid="ts-filter-client"><SelectValue placeholder="All Clients" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Clients</SelectItem>
                    {clients.map(c => (<SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 min-w-[150px]">
                <Label className="text-xs text-slate-500">Status</Label>
                <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
                  <SelectTrigger data-testid="ts-filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[140px]">
                <Label className="text-xs text-slate-500">From</Label>
                <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="ts-filter-date-from" />
              </div>
              <div className="min-w-[140px]">
                <Label className="text-xs text-slate-500">To</Label>
                <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="ts-filter-date-to" />
              </div>
              <Button onClick={handleFilter} className="bg-[#5B6FD6] hover:bg-[#4a5bc5]" data-testid="ts-apply-filter-btn">
                <Filter className="h-4 w-4 mr-1" /> Apply
              </Button>
              <Button variant="outline" onClick={resetFilters} data-testid="ts-reset-filter-btn">
                <RotateCcw className="h-4 w-4 mr-1" /> Reset
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Timesheet Table */}
        <Card className="border-slate-200 shadow-sm bg-white/80" data-testid="timesheet-table">
          <CardHeader>
            <CardTitle className="text-xl font-semibold flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-[#5B6FD6]" />
              Time Entries
              <Badge variant="outline" className="ml-2">{entries.length} entries</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-400">Loading timesheet...</div>
            ) : entries.length === 0 ? (
              <div className="p-8 text-center text-slate-400">
                <Clock className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p>No time entries found. Time logs are recorded when users update task status with hours logged.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-y border-slate-200">
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Member</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Task</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Client</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Start Date</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">End Date</th>
                      <th className="text-center py-3 px-4 font-semibold text-slate-700">Total Hours</th>
                      <th className="text-center py-3 px-4 font-semibold text-slate-700">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry, idx) => {
                      const rowKey = entry.id || `${entry.member_name}-${entry.task_id || entry.task_title}-${entry.start_date || entry.date || idx}`;
                      return (
                      <tr key={rowKey} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`timesheet-row-${idx}`}>
                        <td className="py-3 px-4 font-medium text-slate-900">{entry.member_name}</td>
                        <td className="py-3 px-4">
                          <div>
                            <span className="text-slate-900">{entry.task_title}</span>
                            <Badge variant="outline" className="text-xs ml-2">{entry.service_name}</Badge>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-600">{entry.client_name}</td>
                        <td className="py-3 px-4 text-slate-600 whitespace-nowrap">
                          <div className="flex items-center gap-1"><Calendar className="h-3 w-3" />{entry.start_date || entry.date || '-'}</div>
                        </td>
                        <td className="py-3 px-4 text-slate-600 whitespace-nowrap">
                          {entry.task_status === 'completed' && entry.end_date
                            ? <div className="flex items-center gap-1"><Calendar className="h-3 w-3" />{entry.end_date}</div>
                            : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="text-center py-3 px-4 font-bold text-amber-600">{entry.hours}h</td>
                        <td className="text-center py-3 px-4">
                          <Badge className={`text-xs ${STATUS_COLORS[entry.task_status] || 'bg-slate-100 text-slate-600'}`}>
                            {entry.task_status?.replace(/_/g, ' ')}
                          </Badge>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
    </div>
  );
}
